import React, { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import ExportIcon from '@mui/icons-material/ImportExport';
import type { GraphViewSelectorProps } from './GraphViewControlBar/GraphViewControlBar';

export interface GraphDistribution {
	id: number;
	value: number;
	label: string;
}

interface GraphBoxProps {
	/**
	 * Title of the graph box
	 */
	title: string;

	/**
	 * The chart or graph component to render
	 */
	children: ReactNode;

	/**
	 * Optional height for the graph container
	 * @default 300
	 */
	height?: number | string;

	/**
	 * Full width option for the graph box
	 */
	fullWidth?: boolean;

	/**
	 * Optional custom view selector component
	 * If provided, this will be rendered instead of the default GraphViewControlBar
	 */
	ControlBar?: React.FC<GraphViewSelectorProps>;
}

/**
 * GraphBox component - A container for displaying charts and graphs
 * with a consistent style and layout
 */
const GraphBox: React.FC<GraphBoxProps> = ({ title, children, fullWidth, ControlBar }) => {
	// Reference to export function provided by child chart component
	const [exportFn, setExportFn] = useState<(() => void) | null>(null);

	// Callback for child chart to register its export function
	const registerExportFn = useCallback((fn: () => void) => {
		setExportFn(() => fn);
	}, []);

	// Handle export button click
	const handleExport = useCallback(() => {
		if (exportFn) {
			exportFn();
		}
	}, [exportFn]);
  
	return (
		<Box
			sx={{
				border: '1px solid',
				borderColor: (theme) => theme.palette.divider,
				pb: 3,
				width: {
					xs: '100%',
					md: fullWidth ? '100%' : '48%',
				},
				display: 'flex',
				flexDirection: 'column',
				mb: { xs: 2, md: 3 },
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					backgroundColor: (theme) => theme.palette.background.default,
					px: 1,
					py: 1,
					borderTop: '1px solid',
					borderColor: (theme) => theme.palette.divider,
				}}
			>
				<Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }} data-testid={`${title.replace(/\s+/g, '-').toLowerCase()}-title`}>
					{title}
				</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }} onClick={handleExport}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
						<ExportIcon fontSize="small" />
						<Typography variant="body1" component="span">
							Export
						</Typography>
					</Box>
				</Box>
			</Box>
			{ControlBar && <ControlBar />}
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					height: {
						xs: 'auto',
						md: 300,
					},
					alignItems: 'center',
				}}
			>
				{/* Provide the register function to children via React.cloneElement */}
				{React.isValidElement(children)
					? React.cloneElement(children as React.ReactElement<any>, { registerExportFn })
					: children}
			</Box>
		</Box>
	);
};

export default GraphBox;
